/**
 * components/ShareModal.jsx
 * Approval decision modal — send to reviewer or self-approve.
 * Opened intentionally by the user via the "Ask for Approval" button in the results view.
 * Provides a close/cancel button so users can return and decide later.
 */

import React, { useState } from 'react';
import API from '../services/api';

export default function ShareModal({ drawingId, batchId = null, fileCount = null, drawingName, onDone, onCancel }) {
  const [activeTab,      setActiveTab]      = useState('send');   // 'send' | 'self'
  const [emails,         setEmails]         = useState(['']);     // list of email inputs
  const [emailErrors,    setEmailErrors]    = useState(['']);     // per-input validation error
  const [loading,        setLoading]        = useState(false);
  const [error,          setError]          = useState('');
  const [success,        setSuccess]        = useState('');

  const isBatch = !!batchId;
  const isValidEmail = (e) => /\S+@\S+\.\S+/.test(e.trim());

  const updateEmail = (idx, val) => {
    setEmails(prev => { const n = [...prev]; n[idx] = val; return n; });
    setEmailErrors(prev => { const n = [...prev]; n[idx] = ''; return n; });
  };

  const addEmail = () => {
    setEmails(prev => [...prev, '']);
    setEmailErrors(prev => [...prev, '']);
  };

  const removeEmail = (idx) => {
    setEmails(prev => prev.filter((_, i) => i !== idx));
    setEmailErrors(prev => prev.filter((_, i) => i !== idx));
  };

  const validateEmails = () => {
    const errs = emails.map(e => {
      if (!e.trim()) return 'Email is required.';
      if (!isValidEmail(e)) return 'Enter a valid email address.';
      return '';
    });
    setEmailErrors(errs);
    return errs.every(e => !e);
  };

  const handleSendToReviewer = async () => {
    setError('');
    if (!validateEmails()) return;

    const reviewer_emails = emails.map(e => e.trim());
    setLoading(true);
    try {
      await API.share(
        isBatch
          ? { batch_id: batchId, action: 'send_to_reviewer', reviewer_emails }
          : { drawing_id: drawingId, action: 'send_to_reviewer', reviewer_emails }
      );
      const n = reviewer_emails.length;
      setSuccess(
        isBatch
          ? `Batch review request sent to ${n} reviewer${n > 1 ? 's' : ''}! Each will receive a link to review all ${fileCount} files.`
          : `Review request sent to ${n} reviewer${n > 1 ? 's' : ''}! ${n > 1 ? 'They each' : 'They'} will receive a secure link.`
      );
      setTimeout(() => onDone(), 2000);
    } catch (err) {
      setError(err.error || err.message || 'Failed to send review request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSelfApprove = async () => {
    setError('');
    setLoading(true);
    try {
      await API.share(
        isBatch
          ? { batch_id: batchId, action: 'self_approve' }
          : { drawing_id: drawingId, action: 'self_approve' }
      );
      setSuccess(isBatch ? `Batch self-approved (${fileCount} files).` : 'Drawing self-approved.');
      setTimeout(() => onDone(), 1200);
    } catch (err) {
      setError(err.error || err.message || 'Failed to self-approve. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={s.overlay}>
      <div style={s.modal}>

        {/* Header */}
        <div style={s.header}>
          <div style={s.headerIcon}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#F97316" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
              <path d="M8.59 13.51l6.83 3.98M15.41 6.51l-6.82 3.98"/>
            </svg>
          </div>
          <div style={{ flex: 1 }}>
            <h2 style={s.title}>
              {isBatch ? `Submit Batch Approval (${fileCount} files)` : 'Submit Approval Decision'}
            </h2>
            <p style={s.subtitle}>
              <strong>{drawingName || (isBatch ? `Batch (${fileCount} files)` : `Drawing #${drawingId}`)}</strong> —
              {isBatch
                ? ` each reviewer will see all ${fileCount} files in their own link.`
                : ' send for external review or mark as self-approved.'
              }
            </p>
          </div>
          {onCancel && !success && (
            <button
              onClick={onCancel}
              disabled={loading}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: '#9C9490', fontSize: '1.2rem', lineHeight: 1,
                padding: 4, flexShrink: 0, marginTop: -2,
              }}
              title="Close — you can submit later"
            >
              ✕
            </button>
          )}
        </div>

        {/* Success state */}
        {success ? (
          <div style={s.successBox}>
            <span style={s.successIcon}>✓</span>
            {success}
          </div>
        ) : (
          <>
            {/* Tabs */}
            <div style={s.tabs}>
              <button
                type="button"
                style={{ ...s.tab, ...(activeTab === 'send' ? s.tabActive : s.tabInactive) }}
                onClick={() => { setActiveTab('send'); setError(''); setEmailErrors(emails.map(() => '')); }}
              >
                Send to Reviewer
              </button>
              <button
                type="button"
                style={{ ...s.tab, ...(activeTab === 'self' ? s.tabActive : s.tabInactive) }}
                onClick={() => { setActiveTab('self'); setError(''); }}
              >
                Self Approve
              </button>
            </div>

            {/* Global error */}
            {error && (
              <div style={s.errorBox}>
                <span style={s.errorDot}>!</span>
                {error}
              </div>
            )}

            {/* Send to Reviewer panel */}
            {activeTab === 'send' && (
              <div style={s.panel}>
                <p style={s.desc}>
                  Add one or more reviewer emails. Each reviewer will receive their own secure link
                  to view the inspection plan and submit their approval decision.
                  Combined approval requires <strong>all reviewers</strong> to approve.
                </p>
                <label style={s.label}>Reviewer Email{emails.length > 1 ? 's' : ''}</label>

                {emails.map((email, idx) => (
                  <div key={idx} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 8 }}>
                    <div style={{ flex: 1 }}>
                      <input
                        style={{ ...s.input, ...(emailErrors[idx] ? s.inputError : {}) }}
                        type="email"
                        placeholder="reviewer@company.com"
                        value={email}
                        onChange={e => updateEmail(idx, e.target.value)}
                        disabled={loading}
                        autoFocus={idx === 0}
                      />
                      {emailErrors[idx] && <span style={s.fieldError}>{emailErrors[idx]}</span>}
                    </div>
                    {emails.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeEmail(idx)}
                        disabled={loading}
                        style={s.removeBtn}
                        title="Remove this email"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                ))}

                <button
                  type="button"
                  onClick={addEmail}
                  disabled={loading}
                  style={s.addBtn}
                >
                  + Add another reviewer
                </button>

                <button
                  type="button"
                  style={{ ...s.btn, ...s.btnPrimary, ...(loading ? s.btnDisabled : {}) }}
                  onClick={handleSendToReviewer}
                  disabled={loading}
                >
                  {loading
                    ? <span style={s.spinner} />
                    : `Send to ${emails.length} Reviewer${emails.length > 1 ? 's' : ''} →`}
                </button>
              </div>
            )}

            {/* Self Approve panel */}
            {activeTab === 'self' && (
              <div style={s.panel}>
                <p style={s.desc}>
                  Mark this inspection plan as self-approved. No email will be sent.
                  The approval status will be updated immediately.
                </p>
                <div style={s.selfWarning}>
                  <span>⚠</span>
                  This action confirms that you have reviewed and approved the inspection plan yourself.
                </div>
                <button
                  type="button"
                  style={{ ...s.btn, ...s.btnGrey, ...(loading ? s.btnDisabled : {}) }}
                  onClick={handleSelfApprove}
                  disabled={loading}
                >
                  {loading ? <span style={s.spinner} /> : 'Self Approve'}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/* ── Styles ─────────────────────────────────────────────────────────────────── */
const s = {
  overlay: {
    position: 'fixed', inset: 0,
    backgroundColor: 'rgba(24,18,14,0.7)',
    backdropFilter: 'blur(3px)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 9999,
    padding: 24,
  },
  modal: {
    width: '100%', maxWidth: 480,
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    boxShadow: '0 20px 60px rgba(24,18,14,0.25)',
    overflow: 'hidden',
  },
  header: {
    display: 'flex', alignItems: 'flex-start', gap: 14,
    padding: '24px 24px 20px',
    borderBottom: '1px solid #F2EDE8',
    backgroundColor: '#FFFBF7',
  },
  headerIcon: {
    width: 40, height: 40, borderRadius: 10, flexShrink: 0,
    backgroundColor: 'rgba(249,115,22,0.10)',
    border: '1px solid rgba(249,115,22,0.25)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  title:    { fontSize: '1.05rem', fontWeight: 700, color: '#1A1410', margin: '0 0 4px' },
  subtitle: { fontSize: '0.84rem', color: '#5C5450', margin: 0, lineHeight: 1.4 },

  tabs: {
    display: 'flex', gap: 0,
    backgroundColor: '#F7F4F0',
    borderBottom: '1px solid #E8E2DC',
  },
  tab: {
    flex: 1, padding: '12px 0',
    border: 'none', borderBottom: '2px solid transparent',
    backgroundColor: 'transparent',
    fontSize: '0.88rem', fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.15s',
  },
  tabActive:   { color: '#F97316', borderBottomColor: '#F97316', backgroundColor: '#FFFFFF' },
  tabInactive: { color: '#5C5450' },

  panel: { padding: '20px 24px 24px' },
  desc:  { fontSize: '0.85rem', color: '#5C5450', margin: '0 0 16px', lineHeight: 1.5 },

  label: { display: 'block', fontSize: '0.82rem', fontWeight: 500, color: '#1A1410', marginBottom: 6 },
  input: {
    width: '100%', boxSizing: 'border-box',
    padding: '10px 14px',
    border: '1.5px solid #E8E2DC',
    borderRadius: 10, fontSize: '0.9rem', color: '#1A1410',
    backgroundColor: '#FFFFFF', outline: 'none',
    fontFamily: 'inherit',
    transition: 'border-color 0.15s',
  },
  inputError: { borderColor: '#FCA5A5' },
  fieldError: { display: 'block', fontSize: '0.78rem', color: '#DC2626', marginTop: 4 },

  btn: {
    width: '100%', marginTop: 16, padding: '12px 0',
    border: 'none', borderRadius: 10,
    fontSize: '0.92rem', fontWeight: 600,
    cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    minHeight: 44,
    transition: 'opacity 0.15s',
  },
  btnPrimary:  { backgroundColor: '#F97316', color: '#FFFFFF' },
  btnGrey:     { backgroundColor: '#3D3530', color: '#FFFFFF' },
  btnDisabled: { opacity: 0.65, cursor: 'not-allowed' },

  spinner: {
    width: 18, height: 18,
    border: '2.5px solid rgba(255,255,255,0.3)',
    borderTopColor: '#fff',
    borderRadius: '50%',
    animation: 'spin 0.7s linear infinite',
    display: 'inline-block',
  },

  removeBtn: {
    padding: '10px 12px',
    border: '1.5px solid #E8E2DC',
    borderRadius: 8,
    backgroundColor: '#F7F4F0',
    color: '#9C9490',
    cursor: 'pointer',
    fontSize: '0.85rem',
    lineHeight: 1,
    flexShrink: 0,
    marginTop: 0,
    transition: 'background 0.15s',
  },
  addBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '7px 14px',
    border: '1.5px dashed #F97316',
    borderRadius: 8,
    backgroundColor: 'transparent',
    color: '#F97316',
    cursor: 'pointer',
    fontSize: '0.83rem',
    fontWeight: 500,
    marginBottom: 4,
    transition: 'background 0.15s',
  },

  selfWarning: {
    display: 'flex', alignItems: 'flex-start', gap: 8,
    backgroundColor: '#FFFBEB',
    border: '1px solid #FDE68A',
    borderRadius: 10,
    padding: '10px 14px',
    fontSize: '0.83rem', color: '#92400E',
    lineHeight: 1.4, marginBottom: 4,
  },

  errorBox: {
    display: 'flex', alignItems: 'center', gap: 10,
    backgroundColor: '#FEF2F2',
    border: '1px solid #FCA5A5',
    borderRadius: 8, padding: '10px 24px',
    color: '#DC2626', fontSize: '0.84rem',
  },
  errorDot: {
    width: 20, height: 20, borderRadius: '50%',
    backgroundColor: '#DC2626', color: '#fff',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '0.75rem', fontWeight: 700, flexShrink: 0,
  },

  successBox: {
    display: 'flex', alignItems: 'center', gap: 12,
    margin: 24, padding: '16px 20px',
    backgroundColor: '#F0FDF4',
    border: '1px solid #86EFAC',
    borderRadius: 12,
    fontSize: '0.88rem', color: '#166534', fontWeight: 500,
  },
  successIcon: {
    width: 28, height: 28, borderRadius: '50%',
    backgroundColor: '#16A34A', color: '#fff',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '1rem', fontWeight: 700, flexShrink: 0,
  },
};
